import type { DesignElement } from '@canvas/shared-types';
import { svgDataUrl } from '@/lib/svgDataUrl';

function photo(
  id: string,
  name: string,
  svg: string,
  tags: string[],
  audience: DesignElement['audience'] = 'all',
  w = 640,
  h = 420
): DesignElement {
  const url = svgDataUrl(svg);
  return {
    elementId: id,
    name,
    kind: 'photo',
    category: 'photos',
    tags,
    audience,
    license: 'free',
    preview: { mode: 'image', value: url, label: name },
    defaultWidth: Math.round(w * 0.45),
    defaultHeight: Math.round(h * 0.45),
    factory: { type: 'image-url', url },
  };
}

export const PHOTO_CATALOG: DesignElement[] = [
  photo(
    'photo-teal-horizon',
    'Teal horizon',
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420">
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#9fd4d6"/>
          <stop offset="55%" stop-color="#e8f4f4"/>
          <stop offset="100%" stop-color="#d5e8e0"/>
        </linearGradient>
      </defs>
      <rect width="640" height="420" fill="url(#sky)"/>
      <ellipse cx="480" cy="110" rx="56" ry="56" fill="#f4f0e0" opacity="0.85"/>
      <path d="M0 260 C120 220 220 300 340 250 C460 200 540 280 640 240 L640 420 L0 420 Z" fill="#2f6f72"/>
      <path d="M0 300 C140 270 260 330 400 290 C520 255 580 320 640 300 L640 420 L0 420 Z" fill="#1f4f52"/>
    </svg>`,
    ['landscape', 'calm', 'background']
  ),
  photo(
    'photo-slate-desk',
    'Slate desk',
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420">
      <rect width="640" height="420" fill="#dfe6ee"/>
      <rect x="40" y="60" width="560" height="300" rx="12" fill="#f7f9fc" stroke="#c5d0dc"/>
      <rect x="80" y="100" width="220" height="140" rx="8" fill="#0d7377"/>
      <rect x="330" y="100" width="230" height="28" rx="4" fill="#152033"/>
      <rect x="330" y="148" width="180" height="14" rx="3" fill="#8a97a8"/>
      <rect x="330" y="176" width="200" height="14" rx="3" fill="#aeb8c6"/>
      <rect x="330" y="204" width="140" height="14" rx="3" fill="#aeb8c6"/>
      <circle cx="520" cy="300" r="36" fill="#e5c07b"/>
    </svg>`,
    ['workspace', 'desk', 'professional'],
    'professional'
  ),
  photo(
    'photo-campus-green',
    'Campus green',
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420">
      <rect width="640" height="420" fill="#cfe6d4"/>
      <rect x="0" y="280" width="640" height="140" fill="#6f9f78"/>
      <rect x="80" y="120" width="160" height="180" fill="#f2efe8" stroke="#b8b0a2"/>
      <polygon points="80,120 160,60 240,120" fill="#8b7355"/>
      <rect x="360" y="150" width="200" height="150" fill="#eef2f6" stroke="#9aa8b8"/>
      <rect x="390" y="180" width="50" height="50" fill="#7eb8c9"/>
      <rect x="460" y="180" width="50" height="50" fill="#7eb8c9"/>
      <rect x="430" y="250" width="60" height="50" fill="#45566e"/>
      <circle cx="120" cy="320" r="18" fill="#2d6a4f"/>
      <circle cx="500" cy="310" r="22" fill="#40916c"/>
    </svg>`,
    ['campus', 'school', 'outdoor'],
    'student'
  ),
  photo(
    'photo-abstract-blocks',
    'Abstract blocks',
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420">
      <rect width="640" height="420" fill="#152033"/>
      <rect x="40" y="40" width="220" height="220" fill="#0d7377"/>
      <rect x="280" y="80" width="160" height="160" fill="#e5c07b"/>
      <rect x="460" y="40" width="140" height="300" fill="#45566e"/>
      <rect x="40" y="280" width="400" height="100" fill="#1b4d89"/>
    </svg>`,
    ['abstract', 'brand', 'cover']
  ),
  photo(
    'photo-notebook',
    'Open notebook',
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420">
      <rect width="640" height="420" fill="#c8b8a0"/>
      <rect x="70" y="50" width="240" height="320" fill="#f7f3ea"/>
      <rect x="330" y="50" width="240" height="320" fill="#fffdf8"/>
      <line x1="310" y1="50" x2="310" y2="370" stroke="#8b7355" stroke-width="8"/>
      <g stroke="#d0c6b4" stroke-width="2">
        <line x1="100" y1="100" x2="280" y2="100"/>
        <line x1="100" y1="140" x2="280" y2="140"/>
        <line x1="100" y1="180" x2="260" y2="180"/>
        <line x1="360" y1="100" x2="540" y2="100"/>
        <line x1="360" y1="140" x2="540" y2="140"/>
        <line x1="360" y1="180" x2="520" y2="180"/>
      </g>
      <rect x="400" y="240" width="90" height="70" fill="#0d7377" opacity="0.85"/>
    </svg>`,
    ['study', 'notes', 'stationery'],
    'student'
  ),
  photo(
    'photo-gradient-poster',
    'Poster wash',
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420">
      <defs>
        <linearGradient id="wash" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#1b4d89"/>
          <stop offset="50%" stop-color="#0d7377"/>
          <stop offset="100%" stop-color="#e5c07b"/>
        </linearGradient>
      </defs>
      <rect width="640" height="420" fill="url(#wash)"/>
      <circle cx="520" cy="90" r="70" fill="#ffffff" opacity="0.12"/>
      <circle cx="80" cy="340" r="110" fill="#152033" opacity="0.18"/>
    </svg>`,
    ['poster', 'gradient', 'background']
  ),
  photo(
    'photo-meeting-soft',
    'Soft meeting',
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420">
      <rect width="640" height="420" fill="#eef2f6"/>
      <ellipse cx="200" cy="250" rx="70" ry="90" fill="#8aa0b5"/>
      <circle cx="200" cy="140" r="42" fill="#c9b29b"/>
      <ellipse cx="360" cy="260" rx="70" ry="90" fill="#5f7a8a"/>
      <circle cx="360" cy="145" r="42" fill="#d4b896"/>
      <ellipse cx="500" cy="270" rx="60" ry="80" fill="#45566e"/>
      <circle cx="500" cy="165" r="38" fill="#c2a78a"/>
      <rect x="80" y="330" width="480" height="24" rx="8" fill="#d0dae6"/>
    </svg>`,
    ['people', 'team', 'meeting'],
    'professional'
  ),
  photo(
    'photo-night-grid',
    'Night grid',
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420">
      <rect width="640" height="420" fill="#0f1720"/>
      <g stroke="#1f3a44" stroke-width="1">
        ${Array.from({ length: 12 }, (_, i) => `<line x1="${(i + 1) * 50}" y1="0" x2="${(i + 1) * 50}" y2="420"/>`).join('')}
        ${Array.from({ length: 8 }, (_, i) => `<line x1="0" y1="${(i + 1) * 50}" x2="640" y2="${(i + 1) * 50}"/>`).join('')}
      </g>
      <circle cx="480" cy="120" r="8" fill="#e5c07b"/>
      <circle cx="160" cy="80" r="4" fill="#9fd4d6"/>
      <circle cx="300" cy="200" r="3" fill="#ffffff" opacity="0.7"/>
    </svg>`,
    ['dark', 'tech', 'background']
  ),
];
